import { computed, observable, toJS } from 'mobx';
import { CompositionType, IImage, TBlockType } from '~/types/model.types';
import type { RootStore } from '~/stores/root.store';
import type { QuestionBlock } from '~/models/blocks/question-block.model';
import { ContainerableBlock } from '~/models/blocks/containerable-block.model';
import { CONTENT_BLOCK_TYPES, DnDTypes, TBlock } from '~/types/builder.types';
import {
    ICompanyInfoBlockResponse,
    IContainerBlockResponse,
    ILeadDetailsBlockResponse
} from '~/types/server.types';
import type { FlowRevision } from '~/models/flow-revision.model';

type ContainerBlockDataForServer = ReturnType<
    ContainerableBlock['getDataForServer']
> &
    (
        | Pick<
              IContainerBlockResponse,
              'layout_type' | 'composition_type' | 'background_image' | 'type'
          >
        | Pick<ICompanyInfoBlockResponse, 'type'>
        | Pick<ILeadDetailsBlockResponse, 'type'>
    );

export class ContainerBlock extends ContainerableBlock {
    type: 'container_block' | 'company_info_block' | 'lead_details_block';
    @observable background_image: IImage;
    @observable composition_type: CompositionType;

    constructor(
        block:
            | IContainerBlockResponse
            | ICompanyInfoBlockResponse
            | ILeadDetailsBlockResponse,
        flowRevision: FlowRevision<RootStore>,
        rootStore: RootStore
    ) {
        super(block, flowRevision, rootStore);
        this.type = block.type;
        this.background_image = block.background_image;
        this.composition_type =
            block.composition_type || CompositionType.Default;
    }

    get maxAllowedColumns() {
        return 3;
    }

    get isDeletable() {
        return this.layout_type === 'vertical' ? false : true;
    }

    @computed
    get label(): string {
        return this.isCompanyInfoBlock ? 'Company info' : 'Content';
    }

    @computed
    get shouldAddCompositionMenu() {
        return true;
    }

    @computed
    get allQuestionBlocks() {
        return this.allDescendants().filter(block =>
            block.isQuestionBlock()
        ) as QuestionBlock[];
    }

    @computed
    get shouldExpandOnSelect() {
        return true;
    }

    @computed
    get dndType() {
        return this.isRootChild ? DnDTypes.HorizontalBlock : DnDTypes.Block;
    }

    isContainerBlock(): this is ContainerBlock {
        return true;
    }

    dndAcceptedTypes() {
        return this.dndType;
    }

    canBeParentOf(block: TBlock) {
        if (this.isRootBlock) {
            return block instanceof ContainerableBlock;
        }

        // horizontal container can only be parent of vertical container
        if (this.layout_type === 'horizontal') {
            return (
                block instanceof ContainerBlock &&
                block.layout_type === 'vertical'
            );
        }

        return CONTENT_BLOCK_TYPES.includes(block.type);
    }

    isContainsBlock(blockType: TBlockType) {
        return this.allDescendants().some(block => block.type === blockType);
    }

    @computed
    get isCompanyInfoBlock() {
        return (
            this.type === 'company_info_block' ||
            this.composition_type === CompositionType.CompanyInfo
        );
    }

    @computed
    get linkedFields() {
        return this.allQuestionBlocks
            .filter(block => block.isLinkedQuestion())
            .map(block => block.question.connected_property);
    }

    isDraggable() {
        return !(this.isCompanyInfoBlock && this.page.isPaymentPage);
    }

    get hoverable() {
        return this.layout_type !== 'vertical';
    }

    isBackgroundImagePlaceholer() {
        return this.background_image && !this.background_image.url;
    }

    get shouldAddPadding() {
        return true;
    }

    getDataForServer(): ContainerBlockDataForServer {
        const data: Partial<
            Pick<
                IContainerBlockResponse,
                'composition_type' | 'background_image'
            >
        > = {};

        const isHorizontal = this.layout_type === 'horizontal';
        if (isHorizontal) {
            data.composition_type = this.composition_type;
        }

        // reason we check for page before isRootBlock is to prevent failed access to root_block_id in the method
        // (happens in cross-files content block pasting)
        if (isHorizontal || (this.page && this.isRootBlock)) {
            data.background_image = this.background_image
                ? { ...this.background_image }
                : null;
        }

        return {
            ...super.getDataForServer(),
            type: this.type as 'container_block',
            layout_type: this.layout_type,
            children: toJS(this.children),
            ...data
        };
    }
}
